// src/api/rpFeaturesApi.ts
//
// H-2C2 — the frontend client for the CRM's server-only RP feature endpoint,
// `GET /auth/features`.
//
// SAME-ORIGIN call to the CRM backend (BFF model): the browser holds NO Central
// token — the opaque `crm_sid` cookie travels automatically with
// `credentials: 'include'`. The backend evaluates the flags with its
// server-held token and returns ONLY booleans. This is the RP-path counterpart
// to the legacy Bearer path's `evaluateFeatureFlags` (which POSTs directly to
// Central with a browser-held token the RP path does not have).
//
// Fail-closed: every failure mode (network error, non-200, unparseable body)
// resolves to `null` — the caller treats that as "all flags false" and never
// blocks boot.

const FEATURES_TIMEOUT_MS = 10_000;

/**
 * Fetch evaluated feature flags for the current RP session. Never throws.
 *
 * @param keys - Feature-flag keys to evaluate (forwarded to the BFF, which
 *               evaluates them for the session's workspace).
 * @returns a flat boolean map on success, or `null` on any failure (fail closed).
 */
export async function fetchRpFeatures(
  keys: readonly string[],
): Promise<Record<string, boolean> | null> {
  if (keys.length === 0) return {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FEATURES_TIMEOUT_MS);
  try {
    const query = encodeURIComponent(keys.join(','));
    const res = await fetch(`/auth/features?keys=${query}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-store' },
      signal: controller.signal,
    });
    if (!res.ok) return null;

    const body: unknown = await res.json();
    const flags = (body as { flags?: unknown })?.flags;
    if (!flags || typeof flags !== 'object' || Array.isArray(flags)) return null;

    // Normalize to a strict boolean map — ignore any non-boolean value.
    const out: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(flags as Record<string, unknown>)) {
      if (typeof value === 'boolean') out[key] = value;
    }
    return out;
  } catch {
    // Network error / abort / parse failure — fail closed.
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
